import { useEffect, useRef, useState } from "react";
import { api } from "../api";
import { ConnIcon, TechLevelCards } from "../components";
import { suggestSlug, suggestedToConnect, tourSteps } from "../connections";
import { openConsent } from "../consent";
import { presetTabs, presetPrompt, tabsToCsv } from "../tabs";
import { getTheme } from "../theme";

// The first-run tour: a full page, up to three steps. (1) What hostit is and
// how technical you are; the answer pre-fills the assistant prompt and the
// default tabs (tabs.js presets). (2) Only when the operator marked providers
// or MCP servers suggested: connect them in place, the consent in a popup;
// the ones already connected read as done. (3) Only when control.yml sets welcome-url:
// the operator's own page, framed. Gated on account.onboarded; the profile
// reopens it; "Skip" still marks onboarded so it never nags.
const WelcomeModal = ({ account, refreshAccount, onDone }) => {
  const [step, setStep] = useState(0);
  const [level, setLevel] = useState(account.tech_level || "");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null); // /api/connections, for the suggestions
  const assistantEnabled = !!account.assistant_enabled;
  const welcomeURL = account.welcome_url || "";
  // Tell the operator's page how to blend in: the appearance the viewer chose
  // (system | dark | light) and the exact background and accent colors of the
  // tour, so a cross-origin page -- which we cannot make transparent from here
  // -- can match instead. The params ride the query string, preserving any the
  // URL already has.
  const framedURL = (() => {
    if (!welcomeURL) return "";
    const q = new URLSearchParams({ theme: getTheme() });
    try {
      const cs = getComputedStyle(document.documentElement);
      const bg = cs.getPropertyValue("--bg").trim();
      const accent = cs.getPropertyValue("--accent").trim();
      if (bg) q.set("background", bg);
      if (accent) q.set("accent", accent);
    } catch {
      // no computed style (very old browser): theme alone still helps
    }
    return welcomeURL + (welcomeURL.includes("?") ? "&" : "?") + q.toString();
  })();

  // The first load is kept as a promise: the level step must not decide
  // whether it is the last one before the suggestions are known.
  const loading = useRef(null);
  const load = () => {
    loading.current = api
      .get("/api/connections")
      .catch(() => ({ connections: [], providers: [], mcp_servers: [] }))
      .then((d) => {
        setData(d);
        return d;
      });
    return loading.current;
  };
  useEffect(() => {
    load();
  }, []);
  // The tour covers the page; the page must not scroll underneath it.
  useEffect(() => {
    document.body.classList.add("welcome-open");
    return () => document.body.classList.remove("welcome-open");
  }, []);

  // The steps, decided once the suggestions are known (see tourSteps).
  const stepsFor = (d) => tourSteps(d ? suggestedToConnect(d, { includeConnected: true }) : [], welcomeURL);
  const offers = data ? suggestedToConnect(data, { includeConnected: true }) : [];
  const steps = stepsFor(data);
  const current = steps[Math.min(step, steps.length - 1)];
  const last = step >= steps.length - 1;

  const close = async () => {
    await refreshAccount();
    if (onDone) onDone(); // App.jsx unmounts the tour
  };

  // Picking a level persists the profile and marks the account onboarded, so
  // a reload mid-tour never shows the tour again from the start.
  const finishLevel = async (chosen) => {
    setBusy(true);
    const patch = { onboarded: true };
    if (chosen) {
      patch.tech_level = chosen;
      patch.default_tabs = tabsToCsv(presetTabs(chosen, assistantEnabled));
      if (assistantEnabled) patch.assistant_prompt = presetPrompt(chosen);
    }
    try {
      await api.patch("/api/account", patch);
      // A fast click can beat the suggestions: wait for them, or a tour with a
      // connections step would close here as if it had none.
      const d = data || (await loading.current);
      if (stepsFor(d).length === 1) await close();
      else setStep(1);
    } finally {
      setBusy(false);
    }
  };
  const next = async () => {
    if (last) await close();
    else setStep(step + 1);
  };

  return (
    <div className="welcome-page" role="dialog" aria-modal="true" aria-label="Welcome to hostit">
      <div className="welcome-brand" aria-hidden="true">hostit<span className="welcome-cursor" /></div>
      <div className={"welcome-column" + (current === "help" ? " welcome-column-wide" : "")}>
        <div className="welcome-progress" aria-hidden="true">
          <div className="welcome-progress-bar">
            {steps.map((s, i) => <span key={s} className={"welcome-progress-seg" + (i <= step ? " on" : "")} />)}
          </div>
          {steps.length > 1 && (
            <div className="welcome-progress-label">
              <span>Step {step + 1} of {steps.length}</span>
              <span>{{ level: "Welcome", connections: "Connections", help: "Getting help" }[current]}</span>
            </div>
          )}
        </div>
        {current === "level" && (
          <>
            <div className="welcome-hero">
              <h1>Welcome to hostit</h1>
              <p>
                Small web apps, each in its own container with a subdomain and HTTPS. The assistant
                builds them for you, or you deploy your own. Private until you share them. First: how
                technical are you?
              </p>
            </div>
            {/* Picking a card is the answer: it saves and moves on, no button. */}
            <TechLevelCards
              value={level}
              onChange={(chosen) => {
                setLevel(chosen);
                finishLevel(chosen);
              }}
              disabled={busy}
            />
          </>
        )}
        {current === "connections" && (
          <SuggestedConnections offers={offers} existing={data?.connections || []} onChanged={load} onBack={() => setStep(step - 1)} onNext={next} last={last} />
        )}
        {/* Mounted as soon as the tour has a help step, so it loads in the
            background while the viewer is on the earlier steps and never flashes
            white when the step arrives; parked off-screen until then. */}
        {welcomeURL && steps.includes("help") && (
          <iframe
            className={"welcome-frame" + (current === "help" ? "" : " welcome-frame-preload")}
            src={framedURL}
            title="Getting help"
            sandbox="allow-scripts allow-same-origin allow-popups"
          />
        )}
        {current === "help" && (
          <div className="welcome-actions">
            <button type="button" className="btn btn-big" onClick={() => setStep(step - 1)}>Back</button>
            <button type="button" className="btn btn-primary btn-big" onClick={close}>Get started</button>
          </div>
        )}
      </div>
    </div>
  );
};

// The connections step: each suggested provider or server with a Connect
// button. An account or a server that wants authorization opens its consent
// in a popup; a pasted credential is entered inline. Connected ones show as
// done, so the list reads as progress.
const SuggestedConnections = ({ offers, existing, onChanged, onBack, onNext, last }) => {
  const [open, setOpen] = useState(null); // the static provider whose fields are showing
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const connect = async (s) => {
    if (s.kind === "static" && open !== s.name) {
      setOpen(s.name);
      setValues({});
      setError("");
      return;
    }
    setBusy(s.name);
    setError("");
    const body = s.kind === "mcp"
      ? { provider: "mcp", slug: suggestSlug({ name: s.name }, existing), label: s.label, values: { url: s.url }, popup: true }
      : { provider: s.name, slug: suggestSlug({ name: s.name }, existing), label: s.label, values: s.kind === "static" ? values : undefined, popup: true };
    try {
      const result = await openConsent(() => api.post("/api/connections", body));
      if (result.ok === false) setError(`${s.label} was not connected.`);
      setOpen(null);
      await onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy("");
    }
  };

  const missing = (s) => (s.fields || []).some((f) => !f.optional && !(values[f.name] || "").trim());
  return (
    <>
      <div className="welcome-hero">
        <h1>Connect what you use</h1>
        <p>
          Your apps and chats can act on your behalf once you connect an account. One click each;
          every grant is separate and revocable, and the Connections page has more.
        </p>
      </div>
      {error && <p className="err">{error}</p>}
      <ul className="welcome-suggest">
        {offers.map((s) => (
          <li key={s.name} className={"welcome-suggest-row" + (s.connected ? " done" : "")}>
            <ConnIcon url={s.icon_url} size={22} className="welcome-suggest-icon" />
            <div className="welcome-suggest-text">
              <span className="welcome-suggest-label">{s.label}</span>
              {s.short_description || s.help ? <span className="welcome-suggest-desc">{s.short_description || s.help}</span> : null}
              {open === s.name && s.kind === "static" && (
                <div className="welcome-suggest-fields">
                  {(s.fields || []).map((f) => (
                    <label key={f.name} className="conn-field">
                      <span>{f.label}{f.optional ? " (optional)" : ""}</span>
                      <input
                        type={f.secret ? "password" : "text"}
                        value={values[f.name] || ""}
                        onChange={(e) => setValues({ ...values, [f.name]: e.target.value })}
                        placeholder={f.placeholder}
                        aria-label={f.label}
                        disabled={busy === s.name}
                      />
                    </label>
                  ))}
                </div>
              )}
            </div>
            {s.connected ? (
              <span className="welcome-suggest-done">Connected</span>
            ) : (
              <button
                type="button"
                className="btn btn-primary btn-small"
                onClick={() => connect(s)}
                disabled={busy !== "" || (open === s.name && missing(s))}
              >
                {busy === s.name ? "Connecting..." : open === s.name ? "Save" : "Connect"}
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="welcome-actions">
        <button type="button" className="btn btn-big" onClick={onBack} disabled={busy !== ""}>Back</button>
        <button type="button" className="btn btn-primary btn-big" onClick={onNext} disabled={busy !== ""}>
          {last ? "Get started" : "Continue"}
        </button>
      </div>
    </>
  );
};

export default WelcomeModal;
